/**
 * Estimate the floor height of a loaded splat environment by sampling splat
 * positions within an XZ radius of a target point and taking a low percentile
 * of their Y values.
 *
 * "Average Y" is tempting but wrong — it would include everything above the
 * floor (walls, ceiling, props), giving a value near the middle of the room. We
 * want the *floor*, so we sort and pick the Nth percentile from the bottom. A
 * small percentile (default 5%) is robust to the rare splat that strays below
 * the actual surface, while still hugging it.
 *
 * THE RADIUS IS THE WHOLE POINT, and taking it out is a regression that is easy
 * to make and hard to see. The bounding box's `min.y` is the lowest splat
 * ANYWHERE in the room — a light stand's foot, a dip in the cyclorama, a stray —
 * not the floor the character stands on. Measured on Startup_Level: the box
 * minimum is **-0.351** while the floor under the character is **-0.034**, so
 * using the box drops the avatar, the shadow sun and the ground plane 32 cm.
 * Nothing errors; the character simply stands in a hole.
 *
 * Returns the world-space Y of the estimated floor, or null if not enough splats
 * fall in the radius.
 */

/** Sample from a flat XYZ position array that is ALREADY in world space. */
export function floorYFromPositions(positions, count, {
  targetX = 0,
  targetZ = 0,
  radius = 1.5,
  percentile = 0.05,
  minSamples = 20,
} = {}) {
  if (!positions || !(count > 0)) return null
  const r2 = radius * radius
  const ys = []
  for (let i = 0; i < count; i++) {
    const dx = positions[i * 3] - targetX
    const dz = positions[i * 3 + 2] - targetZ
    if (dx * dx + dz * dz <= r2) ys.push(positions[i * 3 + 1])
  }
  if (ys.length < minSamples) return null
  ys.sort((a, b) => a - b)
  const idx = Math.max(0, Math.min(ys.length - 1, Math.floor(ys.length * percentile)))
  return ys[idx]
}

/**
 * The same estimate from a three splat `BufferGeometry`.
 *
 * Its `position` attribute is already in WORLD space — `loadEnvironmentGeometry`
 * bakes the row's pose into the attributes rather than setting a matrix on the
 * mesh, because the avatar path shares ONE mesh with the environment and a mesh
 * carries a single matrix. So there is no transform to apply here, and applying
 * one would move the samples out from under the character.
 */
export function estimateGeometryFloorY(geometry, options = {}) {
  const attr = geometry?.getAttribute?.('position')
  if (!attr) return null
  return floorYFromPositions(attr.array, attr.count, options)
}
